/**
 * dashboardCacheService.js — 仪表盘 / 素材库列表元数据缓存
 *
 * ══════════════════════════════════════════════════════════════════
 * 🎯 解决了什么问题？
 *
 *   首页图板卡片以前每次刷新都要等 /api/projects 返回才能渲染卡片骨架；
 *   网络抖一下就「半天才出来」。
 *
 *   这个 service 把列表 JSON 持久化到 IndexedDB，下次进首页：
 *     1) 先用上次的快照立刻渲染卡片（< 50ms，0 网络）
 *     2) 同时后台拉新数据，到了再静默替换 state
 *   这就是经典的 stale-while-revalidate 模式。
 *
 * ══════════════════════════════════════════════════════════════════
 * 📐 与其他缓存层的分工
 *
 *   ┌────────────────────────────┬────────────────────────────────┐
 *   │ localCacheService          │ 缓存「图片/视频/音频二进制」    │
 *   │   L0 内存 / L1 IDB / L2 FS │ key = 文件名                    │
 *   ├────────────────────────────┼────────────────────────────────┤
 *   │ projectCacheService        │ 缓存「单个项目的 nodes/edges」  │
 *   │   L1 IDB / L2 FS           │ key = projectId                 │
 *   ├────────────────────────────┼────────────────────────────────┤
 *   │ dashboardCacheService 👈   │ 缓存「列表 JSON」               │
 *   │   只用 L1 IDB              │ key = u:{uid}:{type}[:{crewId}] │
 *   └────────────────────────────┴────────────────────────────────┘
 *
 *   三者互相独立，但配合使用：dashboardCacheService 给出 thumbnail URL
 *   列表，UI 再调 localCacheService 取每张图的 blob。
 *
 * ══════════════════════════════════════════════════════════════════
 * 📦 存储结构
 *
 *   IndexedDB:  CineartDashboardCache (DB_VERSION=1)
 *     └─ object store "lists" (keyPath = key)
 *          { key: string, data: any, updatedAt: number }
 *
 *   key 命名空间一律以 "u:{userId}:" 开头，便于切换账号时按前缀批量清除。
 *   见末尾 keys 工具：
 *     keys.crews(uid)               → "u:42:crews"
 *     keys.projects(uid, crewId)    → "u:42:projects:7"  (crewId 可省，默认 'all')
 *     keys.assets(uid, crewId)      → "u:42:assets:7"
 *
 * ══════════════════════════════════════════════════════════════════
 * ⚙️ 典型用法（在 React 组件里）
 *
 *   useEffect(() => {
 *     const k = dashboardCacheService.keys.projects(user.id);
 *     // 1. 用快照立刻填首屏（如果有）
 *     dashboardCacheService.get(k).then(snap => {
 *       if (snap?.data && stateIsEmpty) setProjects(snap.data);
 *     });
 *     // 2. 后台拉新，覆盖 state + 重写缓存
 *     api.get('/api/projects').then(fresh => {
 *       setProjects(fresh);
 *       dashboardCacheService.put(k, fresh);
 *     });
 *   }, [user.id]);
 *
 * ══════════════════════════════════════════════════════════════════
 * ⚠️ 几条铁律
 *
 *   1) 不要把 blob: URL（localThumbUrl / localCacheUrl）写进缓存
 *      —— blob URL 出了当前 session 就失效，下次读出来会让 <img> 报错。
 *      put() 已用 stripRuntimeFields 自动剔除常见运行时字段，但调用方
 *      仍应避免把 DOM 元素 / 函数 / 类实例等不可序列化对象塞进来。
 *
 *   2) 写操作（删项目、改名、换封面…）后必须更新或失效缓存。
 *      最省心的做法：在监听 state 的 useEffect 里 debounce 300ms 后
 *      put() 一次（DashboardPage 已采用此模式）。
 *
 *   3) 切账号时调 clearForUser(prevUserId)，避免上一个用户的快照在
 *      新账号短暂闪现（DashboardPage / AuthContext 已处理）。
 *
 *   4) DB_VERSION 升级时记得在 onupgradeneeded 里写迁移逻辑。
 * ══════════════════════════════════════════════════════════════════
 */

const DB_NAME = 'CineartDashboardCache';
const DB_VERSION = 1;
const STORE = 'lists';

// 单例 Promise：所有调用共享同一个 IDBDatabase 连接，避免重复 open。
// 出错时置回 null 以便下次 open 重试。
let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => { dbPromise = null; reject(req.error); };
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'key' });
      }
    };
  });
  return dbPromise;
}

/**
 * 读取列表缓存
 *
 * @param {string} key  推荐用 keys.* 工具构造，避免命名漂移
 * @returns {Promise<{key: string, data: any, updatedAt: number} | null>}
 *          没命中返回 null（首次访问 / 已被清空都会到这里）
 *
 * 注意：失败时也返回 null（吞 error），调用方按"无快照"处理即可。
 */
async function get(key) {
  try {
    const db = await openDB();
    return await new Promise((resolve) => {
      const tx = db.transaction([STORE], 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch (e) {
    console.warn('[dashboardCache] get 失败:', e.message);
    return null;
  }
}

/**
 * 写入列表缓存
 *
 * 自动通过 stripRuntimeFields 深拷贝并剔除常见的 blob URL 字段，
 * 否则下次读取会拿到失效的 blob URL（跨会话 / 跨标签页都会失效），
 * 触发 <img> onerror 而不是回退到原始 URL。
 *
 * 写入是 fire-and-forget：失败只 warn，不抛异常 —— 缓存丢一次不影响主流程。
 */
async function put(key, data) {
  try {
    const cleaned = stripRuntimeFields(data);
    const db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    tx.objectStore(STORE).put({ key, data: cleaned, updatedAt: Date.now() });
  } catch (e) {
    console.warn('[dashboardCache] put 失败:', e.message);
  }
}

// 这些字段的值都是 blob: URL 或其他运行时引用，写入缓存等于埋雷。
// 新增字段时记得加进来（或重命名为非 "local" 前缀以便和 stripRuntimeFields 解耦）。
const RUNTIME_FIELDS = ['localThumbUrl', 'localCacheUrl', 'localPosterUrl', 'posterLocalUrl'];

/**
 * 深拷贝 + 剔除运行时字段。
 * - 数组 → 递归处理每个元素
 * - 对象 → 跳过 RUNTIME_FIELDS 中的 key
 * - 其他原始类型 → 原样返回
 *
 * 不处理 Date/Map/Set 等特殊对象（IndexedDB 用 structured clone，原生支持），
 * 此函数只处理对象 / 数组的字段过滤。
 */
function stripRuntimeFields(value) {
  if (Array.isArray(value)) return value.map(stripRuntimeFields);
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) {
      if (RUNTIME_FIELDS.includes(k)) continue;
      out[k] = stripRuntimeFields(value[k]);
    }
    return out;
  }
  return value;
}

/**
 * 删除单个缓存键
 *
 * 适用：明确知道某个 key 已经过期（例如刚删了一个项目）。
 * 大多数场景下 put(key, newData) 比 invalidate(key) 更好用 —— 直接覆盖快照。
 */
async function invalidate(key) {
  try {
    const db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    tx.objectStore(STORE).delete(key);
  } catch (e) {
    console.warn('[dashboardCache] invalidate 失败:', e.message);
  }
}

/**
 * 删除所有以 prefix 开头的键
 *
 * 用 cursor 扫全表（量级一般 < 100 条键，可忽略性能）。
 * 主要用于：
 *   - clearForUser(uid)：批量清除某用户所有列表快照
 *   - 项目删除时：invalidatePrefix(`u:${uid}:assets:${crewId}`)（如有需要）
 */
async function invalidatePrefix(prefix) {
  try {
    const db = await openDB();
    const tx = db.transaction([STORE], 'readwrite');
    const store = tx.objectStore(STORE);
    const req = store.openCursor();
    req.onsuccess = (e) => {
      const cursor = e.target.result;
      if (!cursor) return;
      if (typeof cursor.key === 'string' && cursor.key.startsWith(prefix)) {
        cursor.delete();
      }
      cursor.continue();
    };
  } catch (e) {
    console.warn('[dashboardCache] invalidatePrefix 失败:', e.message);
  }
}

/**
 * 清除某个用户的全部列表缓存
 *
 * 调用时机：登出 / 切换账号。
 * 见 AuthContext.logout()。
 */
async function clearForUser(userId) {
  if (!userId) return;
  await invalidatePrefix(`u:${userId}:`);
}

// ────────────────────────────────────────────────────────────────────
// 便捷 key 构造器
//
// 用法：dashboardCacheService.keys.projects(user.id)
//
// 永远经过这里造 key，避免散落各处的字符串拼接出现拼写漂移。
// crewId 留空时自动用 'all' 占位（部分接口不按 crew 分流）。
// ────────────────────────────────────────────────────────────────────
const keys = {
  crews: (userId) => `u:${userId}:crews`,
  projects: (userId, crewId) => `u:${userId}:projects:${crewId || 'all'}`,
  assets: (userId, crewId) => `u:${userId}:assets:${crewId || 'all'}`,
};

const dashboardCacheService = { get, put, invalidate, invalidatePrefix, clearForUser, keys };
export default dashboardCacheService;
